import React from 'react';
import { Leaf, Settings, Users, BarChart3, Zap, Shield } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import portfolioData from '../data/mock';

const Skills = () => {
  const skillCategories = [
    {
      title: "Agricultura & Agronomía",
      icon: Leaf,
      color: "text-green-600",
      bgColor: "bg-green-100",
      skills: [
        "Liderazgo en Agronomía",
        "Agricultura de Precisión", 
        "Agricultura Regenerativa",
        "Cultivo de Tejidos Vegetales",
        "Sistemas de Cultivo Sustentables"
      ]
    },
    {
      title: "Tecnología & Innovación",
      icon: Settings,
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
      skills: [
        "Implementación ERP",
        "Operación de Drones y Teledetección",
        "Gestión de Innovación",
        "Sistemas Agro-meteorológicos"
      ]
    },
    {
      title: "Liderazgo & Gestión",
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      skills: [
        "Formación y Desarrollo de Equipos",
        "Gestión de Proyectos",
        "Relación con Stakeholders",
        "Optimización de Cadena de Suministro"
      ]
    },
    {
      title: "Calidad & Procesos",
      icon: BarChart3,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      skills: [
        "Certificación ISO 9001",
        "Six Sigma Black Belt",
        "Gestión de Riesgos",
        "Mejora Continua"
      ]
    }
  ];

  return (
    <section id="skills" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-stone-900 mb-6">
            Habilidades y Competencias
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            Una combinación única de expertise técnico, liderazgo operativo y visión estratégica 
            construida a lo largo de 37+ años en el sector agronómico
          </p>
        </div>

        {/* Background Image */}
        <div className="relative mb-16">
          <div className="aspect-[21/9] rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src={portfolioData.images.skillsSection}
              alt="Advanced agricultural technology"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-stone-900/60 to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-start pl-12">
              <div className="text-white max-w-2xl">
                <h3 className="text-3xl font-bold mb-4">
                  Tecnología al Servicio de la Agricultura
                </h3>
                <p className="text-lg opacity-90">
                  Integrando innovación digital con conocimiento agrícola tradicional 
                  para crear soluciones transformadoras
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Skills Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          {skillCategories.map((category, index) => {
            const IconComponent = category.icon;
            return (
              <Card key={index} className="p-8 bg-white border-stone-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`p-3 rounded-xl ${category.bgColor}`}>
                    <IconComponent size={24} className={category.color} />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-900">
                    {category.title}
                  </h3>
                </div>
                
                <div className="space-y-3">
                  {category.skills.map((skill, skillIndex) => (
                    <div key={skillIndex} className="flex items-center justify-between">
                      <span className="text-stone-700 font-medium">{skill}</span>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <div 
                            key={i} 
                            className={`w-2 h-2 rounded-full ${
                              i < 4 ? 'bg-yellow-600' : 'bg-stone-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Certifications & Recognition */}
        <div className="mt-16 text-center">
          <h3 className="text-3xl font-bold text-stone-900 mb-8">
            Certificaciones y Reconocimientos
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Badge variant="outline" className="px-6 py-3 text-lg bg-yellow-600 text-white border-yellow-600">
              <Shield className="mr-2" size={16} />
              ISO 9001:2000 Certified
            </Badge>
            <Badge variant="outline" className="px-6 py-3 text-lg bg-olive-600 text-white border-olive-600">
              <Zap className="mr-2" size={16} />
              Six Sigma Black Belt
            </Badge>
            <Badge variant="outline" className="px-6 py-3 text-lg bg-stone-600 text-white border-stone-600">
              <BarChart3 className="mr-2" size={16} />
              Premio Excelencia Manufactura (2002)
            </Badge>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;